import { useState } from "react";
import type {
  ImagePromptObject,
  GenerationResult,
  StylePreset,
  Engine,
  ImageSize,
  ImageQuality,
} from "./types";
import { Header } from "./components/Header";
import { QuoteInput } from "./components/QuoteInput";
import { PromptReview } from "./components/PromptReview";
import { ResultsGallery } from "./components/ResultsGallery";
import { TypographyMode } from "./components/TypographyMode";

type AppMode = "generate" | "typography";

export default function App() {
  // Mode toggle
  const [mode, setMode] = useState<AppMode>("generate");

  // Global options
  const [stylePreset, setStylePreset] = useState<StylePreset | "auto">("auto");
  const [withText, setWithText] = useState(true);
  const [noMockup, setNoMockup] = useState(true);
  const [engine, setEngine] = useState<Engine>("openai");
  const [size, setSize] = useState<ImageSize>("1024x1024");
  const [quality, setQuality] = useState<ImageQuality>("standard");

  // Data states
  const [prompts, setPrompts] = useState<ImagePromptObject[]>([]);
  const [results, setResults] = useState<GenerationResult[]>([]);
  const [selectedPromptIds, setSelectedPromptIds] = useState<Set<string>>(
    new Set()
  );

  // Loading states
  const [isBuilding, setIsBuilding] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  const handlePromptsBuilt = (builtPrompts: ImagePromptObject[]) => {
    setPrompts(builtPrompts);
    setSelectedPromptIds(new Set(builtPrompts.map((p) => p.id)));
    setError(null);
  };

  const handlePromptUpdate = (updatedPrompt: ImagePromptObject) => {
    setPrompts((prev) =>
      prev.map((p) => (p.id === updatedPrompt.id ? updatedPrompt : p))
    );
  };

  const handleSelectionChange = (promptId: string, selected: boolean) => {
    setSelectedPromptIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(promptId);
      } else {
        next.delete(promptId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedPromptIds(new Set(prompts.map((p) => p.id)));
  };

  const handleSelectNone = () => {
    setSelectedPromptIds(new Set());
  };

  const handleGenerationComplete = (newResults: GenerationResult[]) => {
    setResults((prev) => [...newResults, ...prev]);
    setError(null);
  };

  const clearAll = () => {
    setPrompts([]);
    setResults([]);
    setSelectedPromptIds(new Set());
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Mode Toggle */}
        <div className="mb-8 flex justify-center">
          <div className="inline-flex rounded-lg bg-gray-200 p-1">
            <button
              onClick={() => setMode("generate")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === "generate"
                  ? "bg-white text-gray-900 shadow"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Generate Art
            </button>
            <button
              onClick={() => setMode("typography")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === "typography"
                  ? "bg-white text-gray-900 shadow"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Typography Mode
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {mode === "generate" ? (
          <>
            {/* Quote Input Section */}
            <QuoteInput
              stylePreset={stylePreset}
              setStylePreset={setStylePreset}
              withText={withText}
              setWithText={setWithText}
              noMockup={noMockup}
              setNoMockup={setNoMockup}
              engine={engine}
              setEngine={setEngine}
              size={size}
              setSize={setSize}
              quality={quality}
              setQuality={setQuality}
              onPromptsBuilt={handlePromptsBuilt}
              isBuilding={isBuilding}
              setIsBuilding={setIsBuilding}
              setError={setError}
            />

            {/* Prompt Review Section */}
            {prompts.length > 0 && (
              <PromptReview
                prompts={prompts}
                selectedIds={selectedPromptIds}
                onPromptUpdate={handlePromptUpdate}
                onSelectionChange={handleSelectionChange}
                onSelectAll={handleSelectAll}
                onSelectNone={handleSelectNone}
                onGenerate={handleGenerationComplete}
                size={size}
                quality={quality}
                isGenerating={isGenerating}
                setIsGenerating={setIsGenerating}
                setError={setError}
              />
            )}

            {/* Results Gallery */}
            {results.length > 0 && <ResultsGallery results={results} />}

            {/* Clear All Button */}
            {(prompts.length > 0 || results.length > 0) && (
              <div className="mt-8 text-center">
                <button
                  onClick={clearAll}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Clear all and start over
                </button>
              </div>
            )}
          </>
        ) : (
          <TypographyMode setError={setError} />
        )}
      </main>

      <footer className="py-8 text-center text-sm text-gray-500">
        VisionaryGraphics &copy; {new Date().getFullYear()} Wear Visionary
      </footer>
    </div>
  );
}
